import { FC } from 'react';

import VoucherBatchRow from './VoucherBatchRow.tsx';

import { VoucherSummary, VoucherBatch } from '@/api/voucher/voucher.types.ts';
import VoucherTableHeader from '@/components/layout/vouchers/VoucherTableHeader.tsx';

type VoucherHierarchicalTableProps = {
  batches: VoucherBatch[];
  expandedBatches: Set<string>;
  selectedVouchers: string[];
  isLoading: boolean;
  isGeneratingPdf: boolean;
  isUserCorporate: boolean;
  onToggleBatch: (batchId: string) => void;
  onSelectBatch: (batchId: string, isSelected: boolean) => void;
  onSelectVoucher: (voucherId: string, isSelected: boolean) => void;
  onView: (voucher: VoucherSummary) => void;
  onGeneratePdf: (() => void) | null;
  searchText: string;
  setSearchText: (text: string) => void;
  voucherBatchesCount: number;
  onAddComplimentaryVoucher?: () => void;
};

const columns = [
  { key: 'expand', label: '', width: 'w-4' },
  { key: 'batchName', label: 'Event Name', width: 'w-40' },
  { key: 'voucherCode', label: 'Voucher code', width: 'w-32' },
  { key: 'status', label: 'Status', width: 'w-20' },
  {
    key: 'companyName',
    label: 'Issued by',
    width: 'w-36',
    hideForCorporate: true,
  },
  {
    key: 'companyEmail',
    label: 'Email Address',
    width: 'w-40',
    hideForCorporate: true,
  },
  { key: 'type', label: 'Type', width: 'w-28' },
  { key: 'eventDate', label: 'Event Date', width: 'w-28' },
  { key: 'actions', label: 'Action', width: 'w-16' },
];

const VoucherHierarchicalTable: FC<VoucherHierarchicalTableProps> = ({
  batches,
  expandedBatches,
  selectedVouchers,
  isLoading,
  isGeneratingPdf,
  isUserCorporate,
  onToggleBatch,
  onSelectBatch,
  onSelectVoucher,
  onView,
  onGeneratePdf,
  searchText,
  setSearchText,
  voucherBatchesCount,
  onAddComplimentaryVoucher,
}) => {
  const isAdminUser = !isUserCorporate;
  const displayedColumns = columns.filter(
    (col) => !col.hideForCorporate || isAdminUser
  );

  return (
    <div className="mt-5 mx-4 md:mx-8">
      <VoucherTableHeader
        searchText={searchText}
        setSearchText={setSearchText}
        onGeneratePdf={onGeneratePdf}
        selectedCount={selectedVouchers.length}
        batchCount={voucherBatchesCount}
        onAddComplimentaryVoucher={onAddComplimentaryVoucher}
        isGeneratingPdf={isGeneratingPdf}
      />

      {isLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-100" />
        </div>
      ) : (
        <div className="overflow-x-auto border border-gray-200 rounded-lg shadow-sm opacity-90">
          <table className="w-full table-fixed text-sm">
            <thead className="bg-primary-300 text-white">
              <tr>
                <th className="px-4 py-4 text-left text-sm font-normal tracking-wider w-8">
                  <span className="sr-only">Select</span>
                </th>
                {displayedColumns.map((col) => (
                  <th
                    key={col.key}
                    className={`px-4 py-4 text-left text-sm font-normal tracking-wider ${col.width ?? ''}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody className="bg-white divide-y divide-gray-200">
              {batches.length === 0 ? (
                <tr>
                  <td
                    colSpan={
                      displayedColumns.length + (isUserCorporate ? 1 : 0)
                    }
                    className="text-center py-8 text-gray-500"
                  >
                    {searchText
                      ? 'No vouchers found for the entered code or event name.'
                      : 'No vouchers found'}
                  </td>
                </tr>
              ) : (
                batches.map((batch) => (
                  <VoucherBatchRow
                    key={batch.batchId}
                    batch={batch}
                    expanded={expandedBatches.has(batch.batchId)}
                    selectedVouchers={selectedVouchers}
                    isUserCorporate={isUserCorporate}
                    onToggle={onToggleBatch}
                    onSelectBatch={onSelectBatch}
                    onSelectVoucher={onSelectVoucher}
                    onView={onView}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default VoucherHierarchicalTable;
